import { Fragment } from 'react';

import { Anchor, AppShell, Box, Divider, Group, NavLink as MantineNavLink } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { NavLink, useLocation } from 'react-router-dom';

import { PRODUCT_NAME } from '../../../brand';
import { useAuth } from '../../../contexts/AuthContext';
import { usePropertyOptions } from '../../../hooks/usePropertyOptions';
import { isRestrictedBoardMember } from '../../../lib/boardMemberAccess';
import {
  type RegularNavItem,
  buildAdminNavSections,
  buildRegularNavSections,
  isNavGroup,
  isRadarSubLinkActive,
  radarNavTarget,
} from './navItems';

type LayoutNavbarProps = {
  navbarWidth: number;
  desktopOpened: boolean;
  toggleDesktop: () => void;
  uploadFeatureUnlocked: boolean;
  closeMobile: () => void;
};

/** The test id of a side menu link: the label in lower case, joined by hyphens. */
function navLinkTestId(label: string): string {
  return `layout-navbar-link-${label.toLowerCase().replace(/\s+/g, '-')}`;
}

export function LayoutNavbar({
  navbarWidth,
  desktopOpened,
  toggleDesktop,
  uploadFeatureUnlocked,
  closeMobile,
}: LayoutNavbarProps) {
  const location = useLocation();
  const { user } = useAuth();

  // Check if user has admin profile
  const isAdmin = user?.profiles.includes('admin') || false;
  const isBoardMemberReadOnly = isRestrictedBoardMember(user);
  // Rent Radar is for rental properties: a property whose ownership is known and is a group. The
  // server refuses the rest, so the link must not offer them.
  const { read: propertiesRead } = usePropertyOptions({ kind: 'rentalOwnership' });
  const regularNavSections = buildRegularNavSections({
    isBoardMemberReadOnly,
    uploadFeatureUnlocked,
    radarTarget: radarNavTarget(propertiesRead),
  });
  const adminNavSections = buildAdminNavSections(isAdmin);

  return (
    <>
      {/* Desktop navbar toggle */}
      <Box
        component="button"
        type="button"
        onClick={toggleDesktop}
        visibleFrom="sm"
        aria-label={desktopOpened ? 'Collapse navigation' : 'Expand navigation'}
        aria-expanded={desktopOpened}
        aria-controls="app-navbar"
        style={{
          position: 'fixed',
          left: desktopOpened ? navbarWidth : 0,
          top: 'calc(50vh)',
          transform: 'translateY(-50%)',
          backgroundColor: 'var(--mantine-color-orange-6)',
          color: 'white',
          width: '14px',
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          padding: 0,
          borderRadius: '0 8px 8px 0',
          cursor: 'pointer',
          zIndex: 10,
          transition: 'left 200ms ease',
        }}
      >
        {desktopOpened ? <IconChevronLeft size="1.2rem" /> : <IconChevronRight size="1.2rem" />}
      </Box>

      <AppShell.Navbar
        id="app-navbar"
        p="md"
        style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
      >
        {/* Phone-only: the header gives its logo up so the entity selector fits,
            so the brand lives here instead. */}
        <Box hiddenFrom="sm" mb="sm">
          <img src="/logos/teoai-logo-dark.png" alt={PRODUCT_NAME} style={{ height: '32px' }} />
        </Box>

        {regularNavSections.map((section, sectionIndex) => (
          <Box key={section.key}>
            {sectionIndex > 0 && <Divider my="xs" />}
            {section.items.map((item: RegularNavItem) =>
              isNavGroup(item) ? (
                <MantineNavLink
                  key={item.label}
                  label={item.label}
                  leftSection={<item.icon size="1.2rem" />}
                  defaultOpened={item.children.some((c) => location.pathname === c.to)}
                  style={{
                    minWidth: 0,
                    backgroundColor: 'transparent',
                    borderLeft: '5px solid transparent',
                    borderRadius: 0,
                  }}
                >
                  {item.children.map((child) => (
                    <MantineNavLink
                      key={child.to}
                      data-testid={navLinkTestId(child.label)}
                      component={NavLink}
                      to={child.to}
                      label={child.label}
                      onClick={closeMobile}
                      active={location.pathname === child.to}
                      style={{
                        minWidth: 0,
                        backgroundColor: 'transparent',
                        borderLeft:
                          location.pathname === child.to
                            ? '5px solid var(--mantine-color-orange-6)'
                            : '5px solid transparent',
                        borderRadius: 0,
                      }}
                    />
                  ))}
                </MantineNavLink>
              ) : (
                <Fragment key={item.to}>
                  <MantineNavLink
                    data-testid={navLinkTestId(item.label)}
                    component={NavLink}
                    to={item.to}
                    label={item.label}
                    leftSection={<item.icon size="1.2rem" />}
                    onClick={closeMobile}
                    active={location.pathname === item.to}
                    style={{
                      minWidth: 0,
                      backgroundColor: 'transparent',
                      borderLeft:
                        location.pathname === item.to
                          ? '5px solid var(--mantine-color-orange-6)'
                          : '5px solid transparent',
                      borderRadius: 0,
                    }}
                  />
                  {item.subLink && (
                    <MantineNavLink
                      data-testid={navLinkTestId(item.subLink.label)}
                      component={NavLink}
                      to={item.subLink.to}
                      label={item.subLink.label}
                      onClick={closeMobile}
                      active={isRadarSubLinkActive(location.pathname)}
                      style={{
                        minWidth: 0,
                        backgroundColor: 'transparent',
                        // Under the parent's label: its icon, the icon gap, then one step in.
                        paddingLeft:
                          'calc(var(--mantine-spacing-sm) + 1.2rem + var(--mantine-spacing-sm) + var(--mantine-spacing-md))',
                        borderLeft: isRadarSubLinkActive(location.pathname)
                          ? '5px solid var(--mantine-color-orange-6)'
                          : '5px solid transparent',
                        borderRadius: 0,
                      }}
                    />
                  )}
                </Fragment>
              ),
            )}
          </Box>
        ))}

        {adminNavSections.map((section) => (
          <Box key={section.key} data-testid="layout-navbar-admin-section">
            <Divider label={section.label} labelPosition="center" my="sm" />
            {section.items.map((item) => (
              <MantineNavLink
                key={item.to}
                data-testid="layout-navbar-admin-link"
                component={NavLink}
                to={item.to}
                label={item.label}
                leftSection={<item.icon size="1.2rem" />}
                onClick={closeMobile}
                active={location.pathname === item.to}
                style={{
                  minWidth: 0,
                  backgroundColor: 'transparent',
                  borderLeft:
                    location.pathname === item.to
                      ? '5px solid var(--mantine-color-orange-6)'
                      : '5px solid transparent',
                  borderRadius: 0,
                }}
              />
            ))}
          </Box>
        ))}

        {/* Phone-only: the footer that normally carries these is collapsed below
            sm, and the drawer is the only place left that is always reachable.
            `mt="auto"` pins them under however many nav items the profile has. */}
        <Box hiddenFrom="sm" mt="auto" pt="md">
          <Divider mb="sm" />
          <Group gap="md">
            <Anchor href="/privacy-policy" size="sm" c="inherit" underline="hover" fw={400}>
              Privacy Policy
            </Anchor>
            <Anchor href="/terms-of-use" size="sm" c="inherit" underline="hover" fw={400}>
              Terms of Service
            </Anchor>
          </Group>
        </Box>
      </AppShell.Navbar>
    </>
  );
}
